import { cn, useCurrentTimestamp } from "@tellescope/ui";
import { PointerEvent as ReactPointerEvent, useCallback, useMemo, useRef, useState } from "react";
import { DayColumnEventCell } from "../event-cells";
import { EventsByDateWithLayout } from "../../types";
import { DAY_COLUMN, DRAG_ACTIVATION_DISTANCE_PX, MIN_DURATION_MINUTES, SNAP_INTERVAL_MINUTES } from "../constants";
import { useDroppable } from "@dnd-kit/core";
import {
  complementRanges,
  getZonedDayStart,
  isAvailabilityBlockActiveOn,
  MinuteRange,
  mergeMinuteRanges,
  formatRangeDuration,
} from "../../utils";
import { ActiveDayColumnDrag } from "./useDayColumnDragLogic";
import { usePermissionQuery, useUsersQuery } from "@tellescope/tanstack-query";
import { useAvailabilityRanges } from "../../hooks";
import { isSameDay } from "date-fns/isSameDay";
import { isFuture } from "date-fns/isFuture";

const AVAILABILITY_PADDING = 2 as const;
const EVENT_PADDING = 4 as const;

export interface DayEventsColumnProps {
  droppableId: string;
  droppableData?: Record<string, unknown>;
  dayStartDate: Date;
  events: EventsByDateWithLayout[number] | undefined;
  onTimeClick: (timestamp: number) => void;
  onCreateRange: (startTimestamp: number, durationMinutes: number) => void;
  activeDrag: ActiveDayColumnDrag | null;
  timezone: string;
  dayStartMinutes: number;
  dayEndMinutes: number;
  userIds?: string[];
  showAvailability?: boolean;
}

const snapMinutes = (minutes: number) => Math.round(minutes / SNAP_INTERVAL_MINUTES) * SNAP_INTERVAL_MINUTES;
const pixelsToMinutes = (pixels: number) => pixels / DAY_COLUMN.MINUTE_HEIGHT;

export const DayEventsColumn = ({
  droppableId,
  droppableData,
  dayStartDate,
  events,
  onTimeClick,
  onCreateRange,
  activeDrag,
  timezone,
  dayStartMinutes,
  dayEndMinutes,
  userIds,
  showAvailability,
}: DayEventsColumnProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
    data: droppableData,
  });
  const {
    create: { access: canCreate },
  } = usePermissionQuery({
    model: "calendar_events",
    actions: ["create"],
  });

  const currentTimestamp = useCurrentTimestamp({ pollingRateInMs: 1000 * 60, snapTo: "minute" });
  const availabilityRanges = useAvailabilityRanges({
    userId: userIds?.at(0) ?? "",
    timezone,
    events,
    enabled:
      userIds?.length === 1 &&
      !!userIds?.at(0) &&
      showAvailability &&
      (isSameDay(new Date(), dayStartDate) || isFuture(dayStartDate)),
    referenceDate: currentTimestamp.getTime() > dayStartDate.getTime() ? currentTimestamp : dayStartDate,
  });

  const { data: allUsers } = useUsersQuery();
  const unavailableRanges = useMemo<MinuteRange[]>(() => {
    if (!allUsers) return [];
    const relevantUsers =
      userIds && userIds.length > 0 ? allUsers.filter((user) => userIds.includes(user.id)) : allUsers;
    const allConfiguredBlocks = relevantUsers.flatMap((user) => user.weeklyAvailabilities ?? []);
    if (allConfiguredBlocks.length === 0) return [];
    const activeBlocks = allConfiguredBlocks.filter((block) =>
      isAvailabilityBlockActiveOn(block, dayStartDate, timezone)
    );
    const merged = mergeMinuteRanges(
      activeBlocks.map<MinuteRange>((block) => [block.startTimeInMinutes, block.endTimeInMinutes])
    );
    return complementRanges(merged, dayStartMinutes, dayEndMinutes);
  }, [allUsers, userIds, dayStartDate, timezone, dayStartMinutes, dayEndMinutes]);

  const columnRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{ originY: number; pointerId: number; didDrag: boolean } | null>(null);
  const [creationPreview, setCreationPreview] = useState<{ topPx: number; heightPx: number } | null>(null);

  const visibleMinutes = dayEndMinutes - dayStartMinutes;
  const firstHour = Math.floor(dayStartMinutes / 60);
  const lastHour = Math.ceil(dayEndMinutes / 60);
  const hours = useMemo(
    () => Array.from({ length: lastHour - firstHour }, (_, i) => firstHour + i),
    [firstHour, lastHour]
  );

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      columnRef.current = node;
      setNodeRef(node);
    },
    [setNodeRef]
  );

  const minutesAtPointer = useCallback(
    (clientY: number) => {
      const rect = columnRef.current?.getBoundingClientRect();
      if (!rect) return 0;
      return Math.max(0, Math.min(visibleMinutes, pixelsToMinutes(clientY - rect.top)));
    },
    [visibleMinutes]
  );

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const target = e.target;
    if ("closest" in target && typeof target.closest === "function" && target.closest("[data-day-event]")) return;
    dragStateRef.current = { originY: e.clientY, pointerId: e.pointerId, didDrag: false };
  }, []);

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragStateRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      const distance = Math.abs(e.clientY - drag.originY);
      if (!drag.didDrag && distance < DRAG_ACTIVATION_DISTANCE_PX) return;
      drag.didDrag = true;
      e.currentTarget.setPointerCapture?.(e.pointerId);

      const startMinutes = snapMinutes(minutesAtPointer(drag.originY));
      const currentMinutes = snapMinutes(minutesAtPointer(e.clientY));
      const top = Math.min(startMinutes, currentMinutes);
      const bottom = Math.max(startMinutes, currentMinutes);
      const height = Math.max(MIN_DURATION_MINUTES, bottom - top);

      setCreationPreview({
        topPx: top * DAY_COLUMN.MINUTE_HEIGHT,
        heightPx: height * DAY_COLUMN.MINUTE_HEIGHT,
      });
    },
    [minutesAtPointer]
  );

  const finalizeGesture = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>, fired: boolean) => {
      const drag = dragStateRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      try {
        e.currentTarget.releasePointerCapture?.(e.pointerId);
      } catch {
        // pointer was never captured
      }

      if (fired) {
        if (drag.didDrag) {
          const startMinutes = snapMinutes(minutesAtPointer(drag.originY));
          const endMinutes = snapMinutes(minutesAtPointer(e.clientY));
          const top = Math.min(startMinutes, endMinutes);
          const bottom = Math.max(startMinutes, endMinutes);
          const duration = Math.max(MIN_DURATION_MINUTES, bottom - top);
          const startTimestamp = dayStartDate.getTime() + (top + dayStartMinutes) * 60_000;
          onCreateRange(startTimestamp, duration);
        } else {
          const minutes = snapMinutes(minutesAtPointer(e.clientY));
          const startTimestamp = dayStartDate.getTime() + (minutes + dayStartMinutes) * 60_000;
          onTimeClick(startTimestamp);
        }
      }

      dragStateRef.current = null;
      setCreationPreview(null);
    },
    [dayStartDate, dayStartMinutes, minutesAtPointer, onCreateRange, onTimeClick]
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      finalizeGesture(e, true);
    },
    [finalizeGesture]
  );

  const onPointerCancel = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      finalizeGesture(e, false);
    },
    [finalizeGesture]
  );

  const isDragSourceColumn = activeDrag?.type === "move" && !!events?.some((event) => event.id === activeDrag.event.id);

  const targetQuarterIndex = useMemo(() => {
    if (!isOver || activeDrag?.type !== "move") return null;
    const eventStartMs = activeDrag.event.startTimeInMS;
    const eventDayStartMs = getZonedDayStart(new Date(eventStartMs), timezone).getTime();
    const originalMinutesIntoDay = (eventStartMs - eventDayStartMs) / 60000;
    const snappedTarget = snapMinutes(originalMinutesIntoDay + activeDrag.deltaMinutes);
    const clamped = Math.max(dayStartMinutes, Math.min(dayEndMinutes - 15, snappedTarget));
    return clamped / 15;
  }, [activeDrag, isOver, timezone, dayStartMinutes, dayEndMinutes]);

  return (
    <div
      ref={setRefs}
      className={cn("relative select-none", isDragSourceColumn ? "overflow-visible z-30" : "overflow-hidden")}
      style={{ height: visibleMinutes * DAY_COLUMN.MINUTE_HEIGHT }}
      onPointerDown={canCreate ? onPointerDown : undefined}
      onPointerMove={canCreate ? onPointerMove : undefined}
      onPointerUp={canCreate ? onPointerUp : undefined}
      onPointerCancel={canCreate ? onPointerCancel : undefined}
    >
      {hours.map((hour) => (
        <div
          key={`${hour}-block`}
          className="absolute left-0 right-0 border-b border-b-gray-200 grid grid-rows-4"
          style={{
            top: (hour * 60 - dayStartMinutes) * DAY_COLUMN.MINUTE_HEIGHT,
            height: DAY_COLUMN.HOUR_BLOCK_HEIGHT,
          }}
        >
          {Array.from({ length: 4 }, (_, quarterIndex) => quarterIndex).map((quarter) => {
            const isTargetQuarter = targetQuarterIndex === hour * 4 + quarter;
            return (
              <div
                key={`${hour}-${quarter}-slot`}
                className={cn(
                  "transition-colors rounded-sm",
                  isTargetQuarter ? "bg-gray-100" : canCreate && "hover:bg-gray-100",
                  quarter === 2 && "border-t border-dashed border-t-gray-200"
                )}
              />
            );
          })}
        </div>
      ))}
      {unavailableRanges.map(([startMin, endMin]) => (
        <div
          key={`unavail-${startMin}-${endMin}`}
          aria-hidden
          className="absolute left-0 right-0 pointer-events-none z-0 bg-[repeating-linear-gradient(-60deg,transparent_0,transparent_5px,rgba(0,0,0,0.06)_5px,rgba(0,0,0,0.06)_6px)]"
          style={{
            top: (startMin - dayStartMinutes) * DAY_COLUMN.MINUTE_HEIGHT,
            height: (endMin - startMin) * DAY_COLUMN.MINUTE_HEIGHT,
          }}
        />
      ))}
      {canCreate && creationPreview && (
        <div
          className="absolute left-1 right-1 rounded-sm bg-primary/20 border border-primary/40 pointer-events-none z-20"
          style={{ top: creationPreview.topPx, height: creationPreview.heightPx }}
        />
      )}
      {events?.map((event) => {
        const baseDuration = event.actualDuration ?? event.durationInMinutes;
        const isResizingThis = activeDrag?.type === "resize" && activeDrag.event.id === event.id;
        const renderedDuration = isResizingThis ? activeDrag.previewMinutes : baseDuration;
        const isCancelled = !!event.cancelledAt || !!event.rescheduledAt;
        const canDrag = !isCancelled;

        const dayStartMs = dayStartDate.getTime();
        const viewportStartMs = dayStartMs + dayStartMinutes * 60_000;
        const viewportEndMs = dayStartMs + dayEndMinutes * 60_000;
        const eventStartMs = event.startTimeInMS;
        const eventEndMs = eventStartMs + renderedDuration * 60_000;
        const visibleStartMs = Math.max(eventStartMs, viewportStartMs);
        const visibleEndMs = Math.min(eventEndMs, viewportEndMs);
        if (visibleEndMs <= visibleStartMs) return null;
        const topMinutes = (visibleStartMs - viewportStartMs) / 60_000;
        const visibleEventMinutes = (visibleEndMs - visibleStartMs) / 60_000;
        const isClippedTop = eventStartMs < viewportStartMs;
        const isClippedBottom = eventEndMs > viewportEndMs;

        return (
          <div
            key={event.id}
            data-day-event
            className="absolute min-h-[24px]"
            style={{
              height: visibleEventMinutes * DAY_COLUMN.MINUTE_HEIGHT,
              width: `${event.layout.widthPercent}%`,
              left: `${event.layout.leftPercent}%`,
              top: topMinutes * DAY_COLUMN.MINUTE_HEIGHT,
              zIndex: Math.round(event.layout.leftPercent) + 1,
            }}
          >
            <DayColumnEventCell
              event={event}
              className={cn(
                "absolute",
                isClippedTop && "rounded-t-none border-t-0",
                isClippedBottom && "rounded-b-none border-b-0"
              )}
              style={{
                top: isClippedTop ? 0 : EVENT_PADDING,
                left: EVENT_PADDING,
                right: EVENT_PADDING,
                bottom: isClippedBottom ? 0 : EVENT_PADDING,
              }}
              onClick={{ to: `.?eventId=${event.id}`, replace: true }}
              draggable={canDrag}
              resizable={canDrag}
              durationOverride={isResizingThis ? activeDrag.previewMinutes : undefined}
              timezone={timezone}
            />
          </div>
        );
      })}
      {availabilityRanges.map((range) => {
        const dayStartMs = dayStartDate.getTime();
        const viewportStartMs = dayStartMs + dayStartMinutes * 60_000;
        const viewportEndMs = dayStartMs + dayEndMinutes * 60_000;
        const rangeStartMs = range.start.getTime();
        const rangeEndMs = range.end.getTime();
        const visibleStartMs = Math.max(rangeStartMs, viewportStartMs);
        const visibleEndMs = Math.min(rangeEndMs, viewportEndMs);
        if (visibleEndMs <= visibleStartMs) return null;
        const topMinutes = (visibleStartMs - viewportStartMs) / 60_000;
        const visibleRangeMinutes = (visibleEndMs - visibleStartMs) / 60_000;

        return (
          <div
            key={`availability-${rangeStartMs}-${rangeEndMs}`}
            aria-hidden
            className="absolute left-0.5 right-0.5 my-0.5 pointer-events-none z-[100] bg-emerald-500/10 border border-emerald-500/30 rounded-sm"
            style={{
              top: topMinutes * DAY_COLUMN.MINUTE_HEIGHT + AVAILABILITY_PADDING,
              height: visibleRangeMinutes * DAY_COLUMN.MINUTE_HEIGHT - (AVAILABILITY_PADDING * 2 + 2),
            }}
          >
            <p className="absolute top-0 right-0 text-[10px] text-emerald-800">
              {formatRangeDuration(range.start, range.end)}
            </p>
          </div>
        );
      })}
    </div>
  );
};
